import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useWithdrawalRedPacketConfig } from '../../features/settings';
import { RED_PACKET_PROVIDERS } from '../../features/settings/hooks/useWithdrawalRedPacketConfig';
import type {
  WithdrawalRedPacketFormDTO,
  WithdrawalRedPacketYaoyaolaAmountDTO,
} from '../../features/settings/types/settings.types';
import { fenToYuan, formatDateTime } from '../../lib/format';

const inputCls =
  'h-10 w-full rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary';
const textareaCls =
  'min-h-28 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary';
const smallButtonCls =
  'inline-flex items-center justify-center rounded-md border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40';

const providerLabels = Object.fromEntries(RED_PACKET_PROVIDERS.map((item) => [item.value, item.label]));
const yaoyaolaTypeOptions = [
  { value: 0, label: '普通红包' },
  { value: 1, label: '企业付款' },
] as const;

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-gray-700">{label}</span>
      {children}
    </label>
  );
}

interface AmountFenInputProps {
  value: number;
  onChange: (value: number) => void;
}

function AmountFenInput({ value, onChange }: AmountFenInputProps) {
  const [text, setText] = useState(String(value));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) {
      setText(String(value));
    }
  }, [focused, value]);

  const commit = (nextText: string) => {
    const trimmed = nextText.trim();
    const nextValue = trimmed === '' ? 0 : Number(trimmed);
    if (Number.isFinite(nextValue)) {
      onChange(Math.max(0, Math.trunc(nextValue)));
    }
  };

  return (
    <input
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      value={text}
      onFocus={(e) => {
        setFocused(true);
        if (text === '0') {
          e.currentTarget.select();
        }
      }}
      onBlur={() => {
        setFocused(false);
        commit(text);
      }}
      onChange={(e) => {
        const next = e.target.value;
        if (!/^\d*$/.test(next)) {
          return;
        }
        setText(next);
        commit(next);
      }}
      className={inputCls}
    />
  );
}

interface JinshujuFormRowProps {
  item: WithdrawalRedPacketFormDTO;
  index: number;
  onChange: <K extends keyof WithdrawalRedPacketFormDTO>(
    index: number,
    key: K,
    value: WithdrawalRedPacketFormDTO[K],
  ) => void;
  onRemove: (index: number) => void;
}

function JinshujuFormRow({ item, index, onChange, onRemove }: JinshujuFormRowProps) {
  return (
    <div className="grid grid-cols-1 gap-3 rounded-xl border border-gray-200 bg-gray-50 p-4 xl:grid-cols-[140px,1fr,90px,70px] xl:items-end">
      <Field label="金额（分）">
        <AmountFenInput value={item.amount_fen} onChange={(value) => onChange(index, 'amount_fen', value)} />
      </Field>
      <Field label={`表单链接（${fenToYuan(item.amount_fen)} 元）`}>
        <input
          type="text"
          value={item.form_url}
          onChange={(e) => onChange(index, 'form_url', e.target.value)}
          className={inputCls}
          placeholder="https://jinshuju.net/f/..."
        />
      </Field>
      <label className="inline-flex items-center gap-2 pb-2 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={item.enabled}
          onChange={(e) => onChange(index, 'enabled', e.target.checked)}
          className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
        />
        启用
      </label>
      <button type="button" onClick={() => onRemove(index)} className={smallButtonCls}>
        删除
      </button>
    </div>
  );
}

interface YaoyaolaAmountRowProps {
  item: WithdrawalRedPacketYaoyaolaAmountDTO;
  index: number;
  onChange: <K extends keyof WithdrawalRedPacketYaoyaolaAmountDTO>(
    index: number,
    key: K,
    value: WithdrawalRedPacketYaoyaolaAmountDTO[K],
  ) => void;
  onRemove: (index: number) => void;
}

function YaoyaolaAmountRow({ item, index, onChange, onRemove }: YaoyaolaAmountRowProps) {
  return (
    <div className="grid grid-cols-1 gap-3 rounded-xl border border-gray-200 bg-gray-50 p-4 xl:grid-cols-[140px,1fr,110px,90px,70px] xl:items-end">
      <Field label="金额（分）">
        <AmountFenInput value={item.amount_fen} onChange={(value) => onChange(index, 'amount_fen', value)} />
      </Field>
      <Field label={`标签（${fenToYuan(item.amount_fen)} 元）`}>
        <input
          type="text"
          value={item.label}
          onChange={(e) => onChange(index, 'label', e.target.value)}
          className={inputCls}
          placeholder="例如 30元"
        />
      </Field>
      <Field label="排序">
        <input
          type="number"
          value={item.sort_order}
          onChange={(e) => onChange(index, 'sort_order', Number(e.target.value) || 0)}
          className={inputCls}
        />
      </Field>
      <label className="inline-flex items-center gap-2 pb-2 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={item.enabled}
          onChange={(e) => onChange(index, 'enabled', e.target.checked)}
          className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
        />
        启用
      </label>
      <button type="button" onClick={() => onRemove(index)} className={smallButtonCls}>
        删除
      </button>
    </div>
  );
}

export function WithdrawalRedPacketPage() {
  const {
    data,
    form,
    loading,
    saving,
    error,
    jinshujuIPWhitelistText,
    updateField,
    updateJinshujuField,
    updateYaoyaolaField,
    updateJinshujuFormField,
    addJinshujuForm,
    removeJinshujuForm,
    updateYaoyaolaAmountField,
    addYaoyaolaAmount,
    removeYaoyaolaAmount,
    moveProvider,
    setJinshujuIPWhitelistText,
    save,
    reload,
  } = useWithdrawalRedPacketConfig();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-6 w-6 rounded-full border-2 border-gray-300 border-t-primary animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-20 text-center">
        <p className="mb-3 text-sm text-red-500">{error}</p>
        <button onClick={reload} className="text-sm text-primary hover:underline">
          重试
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl space-y-6">
      <section className="rounded-xl border border-gray-200 bg-white p-5 space-y-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h2 className="text-base font-medium text-gray-900">红包提现总开关</h2>
            <p className="mt-1 text-sm text-gray-500">
              App 只展示金额，不展示通道；后端按下面的通道优先级选择实际发放方式。
            </p>
          </div>
          <label className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-3 py-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => updateField('enabled', e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
            />
            启用红包提现
          </label>
        </div>

        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-3">
          <Field label="回调域名">
            <input
              type="text"
              value={form.callback_domain}
              onChange={(e) => updateField('callback_domain', e.target.value)}
              className={inputCls}
              placeholder="https://api.aiptyg.com"
            />
          </Field>
          <div>
            <h3 className="text-sm font-medium text-gray-900">通道优先级</h3>
            <p className="mt-1 text-xs text-gray-500">金额重复时按这里的顺序选择通道；已创建订单后不自动切换通道。</p>
          </div>
          <div className="space-y-2">
            {form.provider_priority.map((provider, index) => (
              <div key={provider} className="flex items-center justify-between gap-3 rounded-md border border-gray-200 bg-white px-3 py-2">
                <div className="text-sm text-gray-700">
                  <span className="mr-2 text-xs text-gray-400">#{index + 1}</span>
                  {providerLabels[provider] ?? provider}
                </div>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => moveProvider(index, -1)} disabled={index === 0} className={smallButtonCls}>
                    上移
                  </button>
                  <button
                    type="button"
                    onClick={() => moveProvider(index, 1)}
                    disabled={index === form.provider_priority.length - 1}
                    className={smallButtonCls}
                  >
                    下移
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="rounded-xl border border-gray-200 bg-white p-5 space-y-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h2 className="text-base font-medium text-gray-900">金数据表单通道</h2>
            <p className="mt-1 text-sm text-gray-500">保留原有表单链接和 IP 白名单配置。金额可与其他通道重复。</p>
          </div>
          <label className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-3 py-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={form.jinshuju.enabled}
              onChange={(e) => updateJinshujuField('enabled', e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
            />
            启用金数据
          </label>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Field label="金数据回调 IP 白名单">
            <textarea
              value={jinshujuIPWhitelistText}
              onChange={(e) => setJinshujuIPWhitelistText(e.target.value)}
              className={textareaCls}
              placeholder="每行或逗号分隔一个 IP；留空表示不限制"
            />
          </Field>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-sm font-medium text-gray-900">表单链接</h3>
            <button type="button" onClick={addJinshujuForm} className={smallButtonCls}>
              新增表单
            </button>
          </div>
          <div className="space-y-3">
            {form.jinshuju.forms.map((item, index) => (
              <JinshujuFormRow
                key={`jinshuju-form-${index}`}
                item={item}
                index={index}
                onChange={updateJinshujuFormField}
                onRemove={removeJinshujuForm}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="rounded-xl border border-gray-200 bg-white p-5 space-y-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h2 className="text-base font-medium text-gray-900">摇摇啦微信直发通道</h2>
            <p className="mt-1 text-sm text-gray-500">用户在微信内授权后，后端直接按订单金额发放红包。</p>
          </div>
          <label className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-3 py-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={form.yaoyaola.enabled}
              onChange={(e) => updateYaoyaolaField('enabled', e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
            />
            启用摇摇啦
          </label>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Field label="UID">
            <input
              type="text"
              value={form.yaoyaola.uid}
              onChange={(e) => updateYaoyaolaField('uid', e.target.value)}
              className={inputCls}
              placeholder="摇摇啦分配的客户编号"
              autoComplete="off"
            />
          </Field>
          <Field label="API Key">
            <input
              type="password"
              value={form.yaoyaola.api_key ?? ''}
              onChange={(e) => updateYaoyaolaField('api_key', e.target.value)}
              className={inputCls}
              placeholder={data?.yaoyaola?.api_key_configured ? '已配置，留空表示不修改' : '请输入 API Key'}
              autoComplete="new-password"
            />
          </Field>
          <Field label="后端接口域名">
            <input
              type="text"
              value={form.yaoyaola.backend_base_url}
              onChange={(e) => updateYaoyaolaField('backend_base_url', e.target.value)}
              className={inputCls}
              placeholder="https://www.yaoyaola.net"
            />
          </Field>
          <Field label="授权域名缓存秒数">
            <input
              type="number"
              min={0}
              value={form.yaoyaola.auth_domain_cache_seconds}
              onChange={(e) => updateYaoyaolaField('auth_domain_cache_seconds', Number(e.target.value) || 0)}
              className={inputCls}
            />
          </Field>
          <Field label="红包类型 type">
            <select
              value={form.yaoyaola.type}
              onChange={(e) => updateYaoyaolaField('type', Number(e.target.value) || 0)}
              className={inputCls}
            >
              {yaoyaolaTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <Field label="活动名称 title">
            <input
              type="text"
              value={form.yaoyaola.title}
              onChange={(e) => updateYaoyaolaField('title', e.target.value)}
              className={inputCls}
              placeholder="提现红包"
            />
          </Field>
          <Field label="发送方 sendname">
            <input
              type="text"
              value={form.yaoyaola.sendname}
              onChange={(e) => updateYaoyaolaField('sendname', e.target.value)}
              className={inputCls}
              placeholder="平台名称"
            />
          </Field>
          <Field label="祝福语 wishing">
            <input
              type="text"
              value={form.yaoyaola.wishing}
              onChange={(e) => updateYaoyaolaField('wishing', e.target.value)}
              className={inputCls}
              placeholder="提现到账"
            />
          </Field>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Field label={`单笔下限（分，¥${fenToYuan(form.yaoyaola.min_amount_fen)}）`}>
            <input
              type="number"
              min={0}
              value={form.yaoyaola.min_amount_fen}
              onChange={(e) => updateYaoyaolaField('min_amount_fen', Number(e.target.value) || 0)}
              className={inputCls}
            />
          </Field>
          <Field label={`单笔上限（分，¥${fenToYuan(form.yaoyaola.max_amount_fen)}，${form.yaoyaola.type === 1 ? '企业付款最高 1000 元' : '普通红包最高 200 元'}）`}>
            <input
              type="number"
              min={0}
              value={form.yaoyaola.max_amount_fen}
              onChange={(e) => updateYaoyaolaField('max_amount_fen', Number(e.target.value) || 0)}
              className={inputCls}
            />
          </Field>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-medium text-gray-900">自定义金额列表</h3>
              <p className="mt-1 text-xs text-gray-500">排序值越小越靠前；允许与金数据金额重复。</p>
            </div>
            <button type="button" onClick={addYaoyaolaAmount} className={smallButtonCls}>
              新增金额
            </button>
          </div>
          <div className="space-y-3">
            {form.yaoyaola.amounts.map((item, index) => (
              <YaoyaolaAmountRow
                key={`yaoyaola-amount-${index}`}
                item={item}
                index={index}
                onChange={updateYaoyaolaAmountField}
                onRemove={removeYaoyaolaAmount}
              />
            ))}
            {form.yaoyaola.amounts.length === 0 && (
              <div className="rounded-md border border-dashed border-gray-300 px-4 py-6 text-center text-sm text-gray-500">
                暂未配置摇摇啦金额
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="rounded-xl border border-gray-200 bg-white p-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="text-sm text-gray-500">
          <div>最近更新时间：{data?.updated_at ? formatDateTime(data.updated_at) : '未保存到数据库'}</div>
          <div>最近更新人：{data?.updated_by ?? '-'}</div>
        </div>
        <button
          type="button"
          onClick={() => void save()}
          disabled={saving}
          className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-primary/90 disabled:opacity-60"
        >
          {saving && <span className="h-4 w-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />}
          {saving ? '保存中...' : '保存并立即生效'}
        </button>
      </section>
    </div>
  );
}
